import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Phone, Video, MoreVertical, Send, Paperclip, Camera, Mic,
  Check, CheckCheck, Image, FileText, MapPin, Music, User,
  Package, CreditCard, ChevronDown
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

interface WhatsAppPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  selectedNode?: any;
  flow?: any;
}

const WhatsAppPreview: React.FC<WhatsAppPreviewProps> = memo(({ isOpen, onClose, selectedNode, flow }) => {
  const [messages, setMessages] = useState<Array<{
    id: string;
    text?: string;
    type: 'sent' | 'received' | 'system';
    time: string;
    status?: 'sent' | 'delivered' | 'read';
    media?: { type: string; url?: string; caption?: string };
    buttons?: Array<{ id: string; text: string }>;
    location?: { lat: number; lng: number };
    contact?: { name: string; phone: string };
  }>>([
    {
      id: '1',
      text: 'Benvenuto! Questa è un\'anteprima del tuo flusso WhatsApp.',
      type: 'system',
      time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    }
  ]);

  const [inputText, setInputText] = useState('');

  // Simulate message based on selected node
  React.useEffect(() => {
    if (selectedNode?.data) {
      const { actionType, message, config } = selectedNode.data;
      
      if (actionType === 'send_message' && (message || config?.message)) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: message || config.message,
          type: 'sent',
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          status: 'read'
        }]);
      } else if (actionType === 'send_media' && config?.mediaUrl) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'sent',
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          status: 'delivered',
          media: {
            type: 'image',
            url: config.mediaUrl,
            caption: config.mediaCaption
          }
        }]);
      } else if (actionType === 'send_button' && config?.buttons) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: config.message,
          type: 'sent',
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          status: 'read',
          buttons: config.buttons
        }]);
      } else if (actionType === 'send_location' && config?.latitude) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'sent',
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          status: 'delivered',
          location: {
            lat: config.latitude,
            lng: config.longitude
          }
        }]);
      }
    }
  }, [selectedNode]);

  const handleSendMessage = () => {
    if (inputText.trim()) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text: inputText,
        type: 'received',
        time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
      }]);
      setInputText('');
    }
  };

  const renderMessage = (message: typeof messages[0]) => {
    if (message.type === 'system') {
      return (
        <div className="flex justify-center my-2">
          <div className="bg-gray-700 text-xs text-gray-300 px-3 py-1 rounded-full">
            {message.text}
          </div>
        </div>
      );
    }

    const isReceived = message.type === 'received';
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${isReceived ? 'justify-start' : 'justify-end'} mb-2`}
      >
        <div className={`max-w-[70%] ${isReceived ? 'bg-gray-700' : 'bg-whatsapp-message'} rounded-lg p-2 shadow-md`}>
          {/* Media Content */}
          {message.media && (
            <div className="mb-2">
              {message.media.type === 'image' && (
                <div className="relative">
                  <div className="w-48 h-32 bg-gray-600 rounded flex items-center justify-center">
                    <Image className="text-gray-400" size={40} />
                  </div>
                  {message.media.caption && (
                    <p className="text-white text-sm mt-1">{message.media.caption}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Location */}
          {message.location && (
            <div className="w-48 h-32 bg-gray-600 rounded flex items-center justify-center">
              <MapPin className="text-gray-400" size={40} />
              <div className="ml-2 text-white text-sm">
                <p>Posizione</p>
                <p className="text-xs text-gray-300">Lat: {message.location.lat.toFixed(4)}</p>
                <p className="text-xs text-gray-300">Lng: {message.location.lng.toFixed(4)}</p>
              </div>
            </div>
          )}

          {/* Text Content */}
          {message.text && (
            <p className="text-white text-sm">{message.text}</p>
          )}

          {/* Buttons */}
          {message.buttons && (
            <div className="mt-2 space-y-1">
              {message.buttons.map((button) => (
                <button
                  key={button.id}
                  className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white text-sm rounded transition-colors"
                >
                  {button.text}
                </button>
              ))}
            </div>
          )}

          {/* Message Info */}
          <div className="flex items-center justify-end mt-1 gap-1">
            <span className="text-xs text-gray-400">{message.time}</span>
            {!isReceived && message.status && (
              <>
                {message.status === 'sent' && <Check size={12} className="text-gray-400" />}
                {message.status === 'delivered' && <CheckCheck size={12} className="text-gray-400" />}
                {message.status === 'read' && <CheckCheck size={12} className="text-blue-400" />}
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9998]" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999]">
          <Dialog.Title className="sr-only">WhatsApp Preview</Dialog.Title>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="w-[360px] h-[640px] bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Phone Status Bar */}
            <div className="bg-gray-800 px-4 py-1 flex justify-between items-center text-xs text-gray-400">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-400 rounded-sm"></div>
              </div>
            </div>

            {/* WhatsApp Header */}
            <div className="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <ChevronDown size={24} />
                </button>
                <div className="w-10 h-10 bg-gray-600 rounded-full"></div>
                <div>
                  <h3 className="text-white font-medium">Anteprima Chat</h3>
                  <p className="text-xs text-gray-400">online</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Video className="text-gray-400" size={20} />
                <Phone className="text-gray-400" size={20} />
                <MoreVertical className="text-gray-400" size={20} />
              </div>
            </div>

            {/* Messages Area */}
            <div 
              className="flex-1 overflow-y-auto p-4"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='chat-pattern' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='25' cy='25' r='2' fill='%23374151' opacity='0.3'/%3E%3Ccircle cx='75' cy='75' r='2' fill='%23374151' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23chat-pattern)'/%3E%3C/svg%3E")`,
                backgroundColor: '#111827'
              }}
            >
              <AnimatePresence>
                {messages.map((message) => (
                  <div key={message.id}>
                    {renderMessage(message)}
                  </div>
                ))}
              </AnimatePresence>
            </div>

            {/* Input Area */}
            <div className="bg-gray-800 p-3 flex items-center gap-3 border-t border-gray-700">
              <Paperclip className="text-gray-400 cursor-pointer hover:text-white transition-colors" size={20} />
              <Camera className="text-gray-400 cursor-pointer hover:text-white transition-colors" size={20} />
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Scrivi un messaggio"
                className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-full text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-whatsapp-green"
              />
              <button
                onClick={handleSendMessage}
                className="text-whatsapp-green hover:text-whatsapp-light transition-colors"
              >
                {inputText ? <Send size={20} /> : <Mic size={20} />}
              </button>
            </div>
          </motion.div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

WhatsAppPreview.displayName = 'WhatsAppPreview';

export default WhatsAppPreview;
